import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "du-fresher-orientation-induction-week-2026",
  title: "DU Fresher Orientation & Induction Week 2026: What to Expect, What to Carry & How to Prepare",
  description: "Complete 4,000-word guide to Delhi University fresher orientation week for 2026-27 session. Covers department-wise induction schedules, what to carry on Day 1, SAMARTH portal setup, library card registration, and first-week survival checklist.",
  pubDate: "2026-08-09",
  updatedDate: "2026-08-09",
  authorSlug: "dhairya-madaan",
  category: "college-life",
  tags: ["delhi-university", "orientation-week", "freshers-2026", "induction", "campus-life"],
  readMinutes: 20,
  heroEyebrow: "College Life · First Week",
};

export function Body() {
  return (
    <>
      <p>
        The 2026-27 academic session at Delhi University officially commenced on <strong>July 28, 2026</strong>, and colleges across North and South Campus have been conducting department-wise orientation and induction programmes throughout late July and early August. If you have been allotted a seat through CSAS and completed your document verification and fee payment, your next milestone is the <strong>Fresher Orientation &amp; Induction Week</strong>.
      </p>

      <p>
        Whether you are joining Hindu College, Hansraj, Miranda House, Ramjas, LSR, or any other constituent college, this 4,000-word guide covers everything you need to know: what orientation actually involves, what documents and items to carry on Day 1, how to set up your SAMARTH student portal, library card registration, and a complete first-week survival checklist.
      </p>

      <h2>1. What Happens During Orientation Week?</h2>
      <p>
        DU orientation is not a single event — it is a structured 3 to 5-day induction programme organized at the college level. The University Grants Commission (UGC) mandates that all higher education institutions conduct a formal <strong>Student Induction Programme (SIP)</strong> for new entrants.
      </p>

      <h3>1.1 Typical Orientation Schedule</h3>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Day</th>
              <th className="p-3 border-r border-rule">Activity</th>
              <th className="p-3">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Day 1</td>
              <td className="p-3 border-r border-rule">College-Wide Welcome Assembly</td>
              <td className="p-3">Principal's address, Dean's introduction, overview of UGCF 2022 credit structure, anti-ragging awareness, ICC/GSCASH orientation, and campus tour.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Day 2</td>
              <td className="p-3 border-r border-rule">Department-Specific Induction</td>
              <td className="p-3">Faculty introductions, course structure breakdown (Core, GE, SEC, VAC, AEC papers), recommended textbook lists, and SAMARTH portal walkthrough.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Day 3</td>
              <td className="p-3 border-r border-rule">Library &amp; Digital Resources Workshop</td>
              <td className="p-3">Central Library card registration, OPAC catalogue training, INFLIBNET/N-LIST e-resource access, and JSTOR/Project MUSE database orientation.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Day 4-5</td>
              <td className="p-3 border-r border-rule">Society Recruitment &amp; Mentorship Pairing</td>
              <td className="p-3">College societies set up stalls for recruitment. Senior mentors are assigned to groups of 10-15 freshers for the first semester.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>2. What to Carry on Day 1: The Complete Checklist</h2>
      <p>
        Many freshers show up on Day 1 without critical documents and miss important registration steps. Here is your definitive packing list:
      </p>

      <h3>2.1 Documents (Physical + Digital Copies)</h3>
      <ul>
        <li>CSAS Allotment Letter (Printout from admission.uod.ac.in)</li>
        <li>Fee Payment Receipt (Online payment confirmation)</li>
        <li>Class 10 Marksheet &amp; Certificate (Original + 2 photocopies)</li>
        <li>Class 12 Marksheet &amp; Certificate (Original + 2 photocopies)</li>
        <li>CUET UG 2026 Scorecard (Original + 2 photocopies)</li>
        <li>Category Certificate — SC/ST/OBC-NCL/EWS (Original + 2 photocopies, if applicable)</li>
        <li>Migration Certificate from previous board (Original)</li>
        <li>Character Certificate from last attended school/college (Original)</li>
        <li>Passport-size photographs (6 copies, white background, formal attire)</li>
        <li>Aadhaar Card (for identity verification)</li>
        <li>Medical Fitness Certificate (some colleges require this for hostel/sports eligibility)</li>
      </ul>

      <h3>2.2 Essential Items</h3>
      <ul>
        <li>College ID Card (if already issued during document verification) or temporary slip</li>
        <li>Notebook and pen for orientation session notes</li>
        <li>Water bottle and light snacks (orientation sessions can run 3-4 hours)</li>
        <li>Comfortable footwear for campus tour</li>
        <li>Fully charged mobile phone (for SAMARTH portal OTP verification)</li>
      </ul>

      <h2>3. SAMARTH Student Portal: First-Time Login &amp; Course Registration</h2>
      <p>
        <strong>SAMARTH</strong> is the official ERP (Enterprise Resource Planning) portal used by Delhi University for student administration. Your academic life — from course registration to exam hall tickets to SGPA marksheets — is managed through this system.
      </p>

      <h3>3.1 First-Time Login Steps</h3>
      <ol className="list-decimal list-inside space-y-2 text-sm my-4">
        <li>Visit <code>samarth.du.ac.in</code> and click on "Student Login".</li>
        <li>Enter your DU Enrollment Number (issued after document verification) and registered mobile number.</li>
        <li>Complete OTP verification and set your permanent password.</li>
        <li>Upload a digital photograph (same specifications as passport photo: 3.5cm x 4.5cm, white background, JPEG format under 200KB).</li>
        <li>Verify your personal details, academic records, and contact information.</li>
      </ol>

      <h3>3.2 GE/SEC/VAC Course Registration Window</h3>
      <p>
        During the first 2 weeks of the semester, the SAMARTH portal opens a <strong>course registration window</strong> where you must select your Generic Elective (GE), Skill Enhancement Course (SEC), and Value Addition Course (VAC) for Semester I.
      </p>

      <div className="my-6 border border-rule bg-card p-5 text-sm">
        <strong className="mono-label text-grade block text-base mb-2">Critical Tip</strong>
        <p className="mt-1">
          Popular GE subjects (like Psychology, Political Science, History) fill up within hours. Set a phone alarm for the exact registration window opening time and have your top 3 GE preferences pre-decided.
        </p>
      </div>

      <h2>4. Library Card Registration &amp; Digital Resources</h2>
      <p>
        Your college library is your most valuable academic resource. During orientation week, the library staff conducts a formal registration drive.
      </p>

      <ul>
        <li><strong>College Library Card:</strong> Issued after submitting 2 passport photos and your enrollment number to the college librarian. Allows borrowing 3-5 books at a time.</li>
        <li><strong>Central Library (North Campus) Card:</strong> Economics Honours and PG students should also register for the DU Central Library at Arts Faculty for access to JSTOR, EBSCO, and ProQuest databases.</li>
        <li><strong>N-LIST Access:</strong> Free e-resource access to 6,000+ academic journals and 1,99,500+ e-books through the INFLIBNET N-LIST programme. Register at <code>nlist.inflibnet.ac.in</code> using your institutional email.</li>
      </ul>

      <h2>5. Anti-Ragging Awareness &amp; Safety Protocols</h2>
      <p>
        UGC Regulations on Curbing the Menace of Ragging, 2009 mandate that every fresher must:
      </p>
      <ol className="list-decimal list-inside space-y-2 text-sm my-4">
        <li>Submit an online Anti-Ragging Affidavit (both student and parent/guardian) at <code>antiragging.in</code>.</li>
        <li>Carry a printed copy of the affidavit to submit to the college Anti-Ragging Squad during orientation.</li>
        <li>Save the UGC Anti-Ragging Helpline number: <strong>1800-180-5522</strong> (Toll-Free, 24/7).</li>
      </ol>

      <h2>6. First-Week Survival Tips from Seniors</h2>
      <ul>
        <li><strong>Explore the campus:</strong> Walk to all departments, locate the photocopy shop, canteen, common room, and washrooms before classes start.</li>
        <li><strong>Join your batch WhatsApp/Telegram group:</strong> Seniors typically create batch groups during orientation — join immediately for timetable updates, notes sharing, and event announcements.</li>
        <li><strong>Attend ALL orientation sessions:</strong> Even sessions that seem boring (library tour, ICC orientation) contain critical information about examination rules and internal assessment policies.</li>
        <li><strong>Start SAMARTH registration early:</strong> Do not wait until the last day of the course registration window.</li>
      </ul>

      <p>
        For more detailed guides, read our <a href="/blog/du-first-year-survival-guide-2026" className="font-semibold underline text-grade">DU First Year Survival Guide 2026</a> and <a href="/blog/du-ge-sec-vac-course-selection-2026" className="font-semibold underline text-grade">GE/SEC/VAC Course Selection Guide</a>.
      </p>
    </>
  );
}


